import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Alert, Box, Typography,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Backdrop, CircularProgress, Chip,
  useTheme, useMediaQuery,
} from '@mui/material';
import { Download as DownloadIcon } from '@mui/icons-material';
import axios from 'axios';

/**
 * Import clients from a CSV file with merge support.
 *
 * Props:
 *   open            – boolean
 *   onClose         – () => void
 *   onImportSuccess – () => void  (called after a successful import so the parent can refresh)
 */
const ImportClientsCsvDialog = ({ open, onClose, onImportSuccess }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [uploadFile, setUploadFile] = useState(null);
  const [uploadDragOver, setUploadDragOver] = useState(false);
  const [uploadPreviewRows, setUploadPreviewRows] = useState(null);
  const [uploadPreviewLoading, setUploadPreviewLoading] = useState(false);
  const [uploadImporting, setUploadImporting] = useState(false);
  const [uploadImportResult, setUploadImportResult] = useState(null);
  const [uploadError, setUploadError] = useState('');
  const [uploadSuccess, setUploadSuccess] = useState('');

  useEffect(() => {
    if (open) {
      setUploadFile(null);
      setUploadPreviewRows(null);
      setUploadImportResult(null);
      setUploadError('');
      setUploadSuccess('');
    }
  }, [open]);

  const handleClose = () => {
    if (uploadImporting) return;
    setUploadFile(null);
    setUploadPreviewRows(null);
    setUploadImportResult(null);
    setUploadError('');
    onClose();
  };

  const downloadCsvTemplate = async () => {
    try {
      const res = await axios.get('/api/users/client/csv/template', { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'clients_template.csv';
      link.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setUploadError(err.response?.data?.error || 'Failed to download template');
    }
  };

  const handleFileSelected = async (file) => {
    if (!file) { setUploadFile(null); setUploadPreviewRows(null); return; }
    setUploadFile(file);
    setUploadPreviewRows(null);
    setUploadError('');
    setUploadPreviewLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post('/api/users/client/csv/preview', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setUploadPreviewRows(res.data.rows || []);
    } catch (err) {
      setUploadError(err.response?.data?.error || 'Failed to parse file');
      setUploadPreviewRows([]);
    } finally {
      setUploadPreviewLoading(false);
    }
  };

  const handleImportApproved = async () => {
    if (!uploadPreviewRows || uploadPreviewRows.length === 0) return;
    setUploadError('');
    setUploadImporting(true);
    setUploadImportResult(null);
    try {
      const importableRows = uploadPreviewRows.filter((r) => r._status !== 'error');
      const res = await axios.post('/api/users/client/csv/import', { rows: importableRows });
      setUploadImportResult(res.data);
      const { created = 0, updated = 0, errors = [] } = res.data;
      setUploadSuccess(`Import completed: ${created} created, ${updated} updated${errors.length ? `, ${errors.length} failed` : ''}.`);
      if (onImportSuccess) onImportSuccess();
    } catch (err) {
      setUploadError(err.response?.data?.error || 'Import failed');
    } finally {
      setUploadImporting(false);
    }
  };

  const importableCount = (uploadPreviewRows || []).filter((r) => r._status !== 'error').length;
  const errorRowCount = (uploadPreviewRows || []).filter((r) => r._status === 'error').length;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth fullScreen={isMobile}>
      <Backdrop open={uploadImporting} sx={{ zIndex: (t) => t.zIndex.drawer + 1, color: '#fff', flexDirection: 'column', gap: 2 }}>
        <CircularProgress color="inherit" size={48} />
        <Typography variant="h6">Importing clients…</Typography>
      </Backdrop>

      <DialogTitle>Import Clients CSV</DialogTitle>

      <DialogContent>
        {uploadError && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setUploadError('')}>{uploadError}</Alert>}
        {uploadSuccess && <Alert severity="success" sx={{ mb: 2 }}>{uploadSuccess}</Alert>}

        <Button variant="outlined" startIcon={<DownloadIcon />} onClick={downloadCsvTemplate} fullWidth sx={{ mb: 2 }}>
          Download CSV template
        </Button>

        {/* Parsing spinner */}
        {uploadPreviewLoading && (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4, gap: 2 }}>
            <CircularProgress />
            <Typography color="text.secondary">Parsing file…</Typography>
          </Box>
        )}

        {/* Preview table */}
        {!uploadPreviewLoading && uploadPreviewRows && uploadPreviewRows.length > 0 && !uploadImportResult && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
              Preview: {uploadPreviewRows.length} row(s).{' '}
              {importableCount > 0
                ? `${importableCount} will be imported (${uploadPreviewRows.filter(r => r._status === 'create').length} new, ${uploadPreviewRows.filter(r => r._status === 'update').length} update).`
                : ''}
              {errorRowCount > 0 ? ` ${errorRowCount} row(s) have errors and will be skipped.` : ''}
            </Typography>
            <TableContainer sx={{ maxHeight: 360, overflow: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>#</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>Status</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>Email</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>First Name</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>Last Name</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>Phone</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>Company Name</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>VAT ID</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>Invoice External ID</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap', maxWidth: 220 }}>Error</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {uploadPreviewRows.map((row, idx) => {
                    const hasError = row._status === 'error';
                    return (
                      <TableRow key={idx} sx={{ backgroundColor: hasError ? 'rgba(211,47,47,0.08)' : undefined }}>
                        <TableCell>{idx + 1}</TableCell>
                        <TableCell>
                          {row._status === 'create' && <Chip label="New" color="success" size="small" />}
                          {row._status === 'update' && <Chip label="Update" color="info" size="small" />}
                          {row._status === 'error' && <Chip label="Error" color="error" size="small" />}
                        </TableCell>
                        <TableCell>{row.email || '—'}</TableCell>
                        <TableCell>{row.firstName || '—'}</TableCell>
                        <TableCell>{row.lastName || '—'}</TableCell>
                        <TableCell>{row.phone || '—'}</TableCell>
                        <TableCell>{row.companyName || '—'}</TableCell>
                        <TableCell>{row.vatId || '—'}</TableCell>
                        <TableCell>{row.invoiceExternalId || '—'}</TableCell>
                        <TableCell sx={{ maxWidth: 220, color: hasError ? 'error.main' : 'text.secondary', fontSize: '0.75rem' }}>
                          {row._error || '—'}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {/* Import result */}
        {uploadImportResult && (
          <Box sx={{ mt: 2 }}>
            <Alert severity={uploadImportResult.errors?.length > 0 ? 'warning' : 'success'} sx={{ mb: 2 }}>
              {uploadSuccess}
            </Alert>
            {uploadImportResult.errors?.length > 0 && (
              <TableContainer sx={{ maxHeight: 240, overflow: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>Email</TableCell>
                      <TableCell>Error</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {uploadImportResult.errors.map((e, i) => (
                      <TableRow key={i}>
                        <TableCell>{e.row?.email || '—'}</TableCell>
                        <TableCell sx={{ color: 'error.main', fontSize: '0.75rem' }}>{e.error}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 2 }}>
              <Button variant="contained" onClick={handleClose}>Close</Button>
            </Box>
          </Box>
        )}

        {/* Drop zone — shown when no preview and no result */}
        {!uploadPreviewLoading && (!uploadPreviewRows || uploadPreviewRows.length === 0) && !uploadImportResult && (
          <Box
            component="label"
            onDragOver={(e) => { e.preventDefault(); setUploadDragOver(true); }}
            onDragLeave={() => setUploadDragOver(false)}
            onDrop={(e) => {
              e.preventDefault();
              setUploadDragOver(false);
              const file = e.dataTransfer?.files?.[0];
              if (file && (file.name.toLowerCase().endsWith('.csv') || file.type === 'text/csv')) {
                handleFileSelected(file);
              }
            }}
            sx={{
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
              minHeight: 120, mt: 2, px: 2, py: 3, border: '2px dashed',
              borderColor: uploadDragOver ? 'primary.main' : 'divider',
              borderRadius: 2,
              bgcolor: uploadDragOver ? 'action.hover' : 'action.selected',
              cursor: 'pointer', transition: 'border-color 0.2s, background-color 0.2s',
            }}
          >
            <input
              type="file"
              accept=".csv"
              onChange={(e) => handleFileSelected(e.target.files?.[0] || null)}
              style={{ display: 'none' }}
            />
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
              {uploadFile ? uploadFile.name : 'Drag a CSV file here or click to browse'}
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        {!uploadImportResult && (
          <>
            <Button onClick={handleClose}>Cancel</Button>
            {uploadPreviewRows && uploadPreviewRows.length > 0 && !uploadImporting && (
              <Button
                onClick={handleImportApproved}
                variant="contained"
                color="primary"
                disabled={importableCount === 0}
              >
                Import clients
              </Button>
            )}
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ImportClientsCsvDialog;
